/**
 * Phúc khảo 1 câu ở trang xem lại bài (plan 2026-10-03 §4): nút "Phúc khảo câu này" (khi còn
 * hạn + câu đã có điểm), trạng thái đơn, phản hồi của giảng viên, điểm trước/sau (thang 10 —
 * CLAUDE.md §13), rút đơn khi còn chờ xử lý.
 */
import { useState } from "react";
import { Alert, Button, Input, Modal, Space, Typography, message } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { apiClient } from "@/api/client";
import type { AnswerSnapshot } from "@/api/assessment";
import { ScoreTag, pctTo10 } from "@/components/ScoreTag";
import { useAuthStore } from "@/stores/auth";

const { Text, Paragraph } = Typography;

const ALERT_TYPE = {
  pending: "info",
  accepted: "success",
  rejected: "warning",
  withdrawn: "info",
} as const;

function errorText(err: unknown): string {
  const d = (err as { response?: { data?: { detail?: string } } }).response?.data;
  return d?.detail ?? "Không gửi được đơn phúc khảo";
}

export function AppealBox({
  attemptId,
  answer,
}: {
  attemptId: number;
  answer: AnswerSnapshot | undefined;
}) {
  const enabled = useAuthStore((s) => !!s.user?.feature_flags?.grade_appeals);
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const refresh = () => qc.invalidateQueries({ queryKey: ["assessment", "attempt", attemptId] });
  const create = useMutation({
    mutationFn: async () =>
      apiClient.post(`/assessment/attempts/${attemptId}/appeals/`, {
        answer_id: answer?.answer_id,
        reason,
      }),
    onSuccess: () => {
      message.success("Đã gửi đơn phúc khảo — giảng viên sẽ xem xét.");
      setOpen(false);
      setReason("");
      refresh();
    },
    onError: (e) => message.error(errorText(e)),
  });
  const withdraw = useMutation({
    mutationFn: async (id: number) => apiClient.post(`/assessment/appeals/${id}/withdraw/`),
    onSuccess: () => {
      message.info("Đã rút đơn phúc khảo.");
      refresh();
    },
  });

  if (!enabled || !answer) return null;
  const appeal = answer.appeal;
  const to10 = (points: number, max: number) => (max ? pctTo10((points / max) * 100) : 0);

  if (appeal) {
    return (
      <Alert
        style={{ marginTop: 12 }}
        type={ALERT_TYPE[appeal.status]}
        showIcon
        message={
          <Space wrap>
            <Text strong>Phúc khảo: {appeal.status_label}</Text>
            {appeal.status === "accepted" && appeal.new_points != null && (
              <Text>
                Điểm câu <ScoreTag value={to10(appeal.old_points, appeal.max_points)} /> →{" "}
                <ScoreTag value={to10(appeal.new_points, appeal.max_points)} />
              </Text>
            )}
          </Space>
        }
        description={
          <>
            <Paragraph style={{ marginBottom: 4 }}>
              <Text type="secondary">Lý do của bạn: </Text>
              {appeal.reason}
            </Paragraph>
            {appeal.response && (
              <Paragraph style={{ marginBottom: 0 }}>
                <Text type="secondary">Giảng viên phản hồi: </Text>
                {appeal.response}
              </Paragraph>
            )}
          </>
        }
        action={
          appeal.status === "pending" && (
            <Button
              size="small"
              loading={withdraw.isPending}
              onClick={() => withdraw.mutate(appeal.id)}
            >
              Rút đơn
            </Button>
          )
        }
      />
    );
  }

  if (!answer.appeal_open) return null;
  return (
    <>
      <Space style={{ marginTop: 12 }}>
        <Button size="small" onClick={() => setOpen(true)}>
          Phúc khảo câu này
        </Button>
        {answer.appeal_deadline && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Hạn phúc khảo: {dayjs(answer.appeal_deadline).format("HH:mm DD/MM/YYYY")}
          </Text>
        )}
      </Space>
      <Modal
        title="Đơn phúc khảo"
        open={open}
        onCancel={() => setOpen(false)}
        okText="Gửi đơn"
        cancelText="Huỷ"
        okButtonProps={{ disabled: reason.trim().length < 10 }}
        confirmLoading={create.isPending}
        onOk={() => create.mutate()}
        destroyOnHidden
      >
        <Paragraph type="secondary">
          Nêu rõ vì sao bạn cho rằng điểm câu này chưa đúng (ít nhất 10 ký tự). Giảng viên phụ trách
          sẽ xem lại và trả lời; mỗi câu chỉ phúc khảo 1 lần.
        </Paragraph>
        <Input.TextArea
          rows={5}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={4000}
          showCount
        />
      </Modal>
    </>
  );
}
